import type { ProfileId } from '@cixing/shared';

/** バックエンドの HTTP API。Cookie 認証なので credentials を必ず付ける。 */

export interface ProfileInfo {
  id: ProfileId;
  label: string;
  count: number;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // Cookie を送受信するために必須。これが無いとログイン状態が保持されない。
    credentials: 'same-origin',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let message = '通信に失敗しました。';
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // JSON でないエラー応答はそのまま既定メッセージを使う。
    }
    throw new Error(message);
  }

  return (await response.json()) as T;
}

export function login(code: string, scope: 'user' | 'admin' = 'user'): Promise<{ ok: boolean }> {
  return request('/api/auth/login', { method: 'POST', body: JSON.stringify({ code, scope }) });
}

export function logout(): Promise<{ ok: boolean }> {
  return request('/api/auth/logout', { method: 'POST' });
}

export function fetchMe(): Promise<{ authenticated: boolean; isAdmin: boolean }> {
  return request('/api/auth/me');
}

export function fetchProfiles(): Promise<{
  version: string;
  profiles: ProfileInfo[];
  total: number;
  enabled: number;
}> {
  return request('/api/glossary/profiles');
}

export function submitReport(body: unknown): Promise<{ ok: boolean }> {
  return request('/api/reports', { method: 'POST', body: JSON.stringify(body) });
}

// ---- 講義アーカイブ --------------------------------------------------------

export interface LectureMeta {
  lectureId: string;
  title: string;
  ownerDeviceId?: string;
  ownerName?: string;
  startedAt: string;
  updatedAt: string;
  segmentCount: number;
  hasNote: boolean;
}

export interface LectureSegment {
  segmentId: string;
  at: string;
  sourceZh: string;
  translatedJa: string;
  /** 'photo' = 撮影した資料の翻訳。 */
  kind?: 'photo';
  /** kind='photo' のとき、写真のファイル名。 */
  photo?: string;
}

// ---- 用語解説 --------------------------------------------------------------

/** ハイライト用の用語一覧(日本語見出し → 中国語)。 */
export function fetchTerms(): Promise<{ version: string; terms: Record<string, string> }> {
  return request('/api/terms');
}

/** 用語の平易な解説。初回は生成に数秒かかる(以後サーバーキャッシュ)。 */
export function explainTerm(
  ja: string,
  zh?: string,
): Promise<{ ok: boolean; explanation: string; cached: boolean }> {
  return request('/api/terms/explain', { method: 'POST', body: JSON.stringify({ ja, zh }) });
}

// ---- 資料写真の翻訳 --------------------------------------------------------

export interface PhotoTranslateResult {
  ok: boolean;
  pairs: { zh: string; ja: string }[];
  lectureId?: string;
  photo?: string;
}

/** 撮影した資料写真を翻訳する。数十秒かかることがある。 */
export function translatePhoto(body: {
  imageBase64: string;
  clientSessionId?: string;
  deviceId?: string;
  deviceName?: string;
}): Promise<PhotoTranslateResult> {
  return request('/api/photo/translate', { method: 'POST', body: JSON.stringify(body) });
}

export function fetchLectures(deviceId?: string): Promise<{ lectures: LectureMeta[] }> {
  const qs = deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : '';
  return request(`/api/lectures${qs}`);
}

export interface LectureSearchResult {
  meta: LectureMeta;
  matches: { segmentId: string; sourceZh: string; translatedJa: string }[];
  /** AI生成の講義ノート本文に一致した箇所の抜粋。 */
  noteMatches?: string[];
}

export function searchLectures(
  q: string,
  deviceId?: string,
): Promise<{ results: LectureSearchResult[] }> {
  const dev = deviceId ? `&deviceId=${encodeURIComponent(deviceId)}` : '';
  return request(`/api/lectures/search?q=${encodeURIComponent(q)}${dev}`);
}

export function fetchLecture(id: string): Promise<{
  meta: LectureMeta;
  segments: LectureSegment[];
  note?: string;
}> {
  return request(`/api/lectures/${encodeURIComponent(id)}`);
}

export function renameLecture(id: string, title: string): Promise<{ ok: boolean }> {
  return request(`/api/lectures/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
}

/** 講義ノートの生成。LLM 呼び出しを含むため数十秒かかることがある。 */
export function generateLectureNote(id: string): Promise<{ ok: boolean; note: string }> {
  return request(`/api/lectures/${encodeURIComponent(id)}/note`, { method: 'POST' });
}

// ---- 資料Q&A ---------------------------------------------------------------

export type QaSourceType = 'lecture-note' | 'video-summary' | 'doc' | 'weekly-report';

export interface QaSourceRef {
  /** 回答本文の [n] に対応する番号。 */
  n: number;
  chunkId: string;
  sourceType: QaSourceType;
  sourceName: string;
  section: string;
  snippet: string;
  /** 講義ノート由来のときだけ付く。講義メモを開くのに使う。 */
  lectureId?: string;
}

export interface QaAnswer {
  answer: string;
  /** 資料に基づかない一般知識の補足。資料の回答とは別枠で表示する。空文字なら無し。 */
  supplement: string;
  /** 資料から答えられなかった場合 true。出典は空になる。 */
  noAnswer: boolean;
  sources: QaSourceRef[];
  /** 答えられなかったときの「関連しそうな資料」。回答の根拠ではない。 */
  relatedSources: QaSourceRef[];
  suggestedQuestions: string[];
}

export interface QaStatus {
  available: boolean;
  chunkCount: number;
  sourceCount: number;
  byType: Partial<Record<QaSourceType, number>>;
  updatedAt?: string;
}

export function fetchQaStatus(): Promise<QaStatus> {
  return request('/api/qa/status');
}

/** 資料への質問。検索と LLM 呼び出しを含むため十数秒かかることがある。 */
export function askQa(
  question: string,
  history: { q: string; a: string }[] = [],
): Promise<QaAnswer> {
  return request('/api/qa', {
    method: 'POST',
    // 文脈は直近3往復まで。それ以上送っても精度は上がらず、待ち時間だけ延びる。
    body: JSON.stringify({ question, history: history.slice(-3) }),
  });
}
